// Surface 02 — la fiche candidature : l'offre ET son dossier, une seule page.
//
// FUSIONNÉE AVEC L'ANCIENNE SURFACE 03 LE 25 AOÛT 2026. Générer un dossier
// ouvrait une page à part (`#/offre/:id/dossier/:did`), vue une fois puis
// rouverte depuis une liste de « dossiers générés » pour y revenir. Nicolas :
// « je n'aime pas que ça ouvre une nouvelle page qu'on ne revoit plus ». Le
// dossier du candidat sélectionné s'affiche maintenant EN PLACE, dans la
// colonne de droite (`DossierAtelier`) : sélectionner un autre candidat dans
// la colonne de gauche change ce qui s'affiche à droite, sans navigation.
//
// UN DOSSIER COURANT PAR (OFFRE, CANDIDAT), pas une liste. `o.dossiers`
// continue de porter TOUT l'historique en base (principe « on garde tout »,
// jamais un UPDATE) : cette page ne prend que le plus récent par candidat
// pour savoir QUOI afficher, l'historique lui-même se parcourt depuis
// `DossierAtelier`, via son sélecteur de version.
//
// LA QUESTION QUE CET ÉCRAN DOIT TRANCHER n'est pas « ce profil convient-il ? »
// mais « LEQUEL est-ce que je présente ? ». D'où la case « comparer » sur
// chaque profil et le tableau multi-colonnes de `ScoreCompare` (packages/ui)
// dès qu'il y en a deux — gardé en secondaire, il remplace alors le poste de
// travail plutôt que de s'ajouter à lui.
//
// LE SCORE EST COMPACT (44 px, comme le widget), pas le panneau héros de 88 :
// il n'est plus la seule chose à montrer sur cet écran. Son détail se déplie
// à la demande (`ScorePanel compact`).
//
// RIEN N'EST GÉNÉRÉ ICI SANS GESTE EXPLICITE. Changer de profil, en cocher
// trois, ouvrir le journal : tout est déjà en base, instantané et gratuit.
// Seuls « Évaluer » et « Générer le dossier » appellent l'IA.

import { useEffect, useRef, useState } from 'react'
import {
  ChevronLeft, ChevronRight, ExternalLink, Sparkles, Clock, MapPin,
  Rss, History, GitCompare, Users, Send, CalendarDays, Swords, Building2,
  Check, Bookmark, X, CircleAlert, Coins,
} from 'lucide-react'
import { ScorePanel, ScoreCompare, ScoreRing, Checkbox, ExigencesCitees, scoreTone, type CompareSubject } from '@ai-job-fit/ui'
import { AvisOffre } from '../components/avisOffre'
import { premiereVueDesExigences } from '../lib/mouvement'
import { get, post, type OfferDetail, type Candidate, type ConsultantRow, type OfferStatus, type GapQuestion } from '../lib/api'
import { relTime, fullDate, dateHeure, initials, teinte, tempsRestant, offerStatus, jourCourt, parseDate } from '../lib/format'
import { langueVerdict } from '../lib/langueVerdict'
import { lireFile, position, FIN_DE_FILE } from '../lib/file'
import { faitsEnMots, salaireEnMots } from '../../faitsOffre'
import { retourOffres, navigate, parseQuery } from '../lib/router'
import { nettoyerDescription } from '../../texteOffre'
import { useData, Loading, Empty, ActionButton, useToast } from '../components/common'
import { SelecteurEtape, ProposerProfil } from '../components/candidature'
import { QuestionsEcart } from '../components/gapQuestions'
import { BlocRelance } from '../components/relance'
import { BlocEntretien } from '../components/entretien'
import { BlocRemuneration, gesteSalaireOffert, useSalaire } from '../components/remuneration'
import { ParcoursCandidature } from '../components/parcours'
import { dateRappel } from '../lib/prochaineEtape'
import { DossierAtelier } from '../components/dossierAtelier'
import { useFormule, useMonProfil, useProfilUnique } from '../lib/formule'
import { CompteurAdaptations } from '../components/compteurAdaptations'
import { presentationFiche } from '../lib/ficheOffre'
import { Categories } from '../components/categories'
import { useMots } from '../lib/vocabulaire'
import { useEventLabels } from '../lib/dictionnaires'
import { useScoreLabels, useCompareLabels } from '../lib/labels'
import { useLang } from '../lib/lang'
import { useT } from '../lib/i18n'
import { Employeur, LogoEntreprise, jourDeLecture, mentionSignaux } from '../components/sites'

/** Au-delà de quatre colonnes, le tableau ne se lit plus sans défiler : la
 *  comparaison redevient une lecture séquentielle, ce qu'elle doit remplacer. */
const MAX_COMPARE = 4

export function Offre({ id, onChanged }: { id: number; onChanged: () => void }) {
  const EVENEMENTS = useEventLabels()
  const profilUnique = useProfilUnique()
  const vue = presentationFiche(useFormule())
  const formuleRadar = useFormule() === 'radar'
  const [raisonDonnee, setRaisonDonnee] = useState(false)
  const actionLien = parseQuery(window.location.hash).get('a')
  // La file d'offres à trier (issue #170) : portée par l'adresse, depuis un
  // mail de plusieurs offres ou les cartes de l'accueil.
  const enFile = position(id, lireFile(parseQuery(window.location.hash).get('file')))
  const suivanteDansFile = () => navigate(enFile?.suivante ?? FIN_DE_FILE)
  // Radar : le CV s'ouvre en fenêtre, comme dans l'extension. Elle s'ouvre
  // d'elle-même quand le CV vient d'être fait, sur la lettre quand le parcours
  // la demande (« Rédiger la lettre » passe ainsi par les questions d'angle,
  // issue #178), et jamais en revenant plus tard sur la fiche.
  const [fenetreCv, setFenetreCv] = useState<null | 'cv' | 'letter'>(null)
  // Une lettre vient d'être écrite : le parcours relit son étape 2.
  const [lettreEcrite, setLettreEcrite] = useState(0)
  const monProfil = useMonProfil()
  const mots = useMots()
  const t = useT()
  const scoreLabels = useScoreLabels()
  const compareLabels = useCompareLabels()
  const lang = useLang()
  const OFFER_STATUS = offerStatus(useFormule(), lang)
  const offre = useData(() => get<OfferDetail>(`/api/offers/${id}`), [id])
  const vivier = useData(() => get<ConsultantRow[]>('/api/consultants'), [])
  const [selected, setSelected] = useState<number | null>(null)
  const [compare, setCompare] = useState<number[]>([])
  const [ouvert, setOuvert] = useState(false)
  const [generating, setGenerating] = useState<string | null>(null)
  const [pendingQuestions, setPendingQuestions] = useState<{ candidate: Candidate; questions: GapQuestion[] } | null>(null)
  // Une seule fois par (offre, consultant) et par visite de l'écran, comme
  // côté extension (askedJobIds) : redemander à chaque clic sur « Générer »
  // userait la question, pas seulement la première fois.
  const dejaDemande = useRef<Set<number>>(new Set())
  // Réglage « Questions avant de générer » (issue #166) : à « toujours », la
  // mémoire ci-dessus est ignorée.
  const reglages = useData(() => get<{ questions?: string }>('/api/settings'), [])
  const toujoursDemander = reglages.data?.questions === 'toujours'
  const toast = useToast()
  // La rémunération (issue #208) : Radar, pour le profil courant. Lue avant
  // les retours anticipés (règle des hooks), d'où le calcul du courant ici.
  const idCourant = offre.data ? (profilCourant(offre.data.candidates, selected)?.consultant_id ?? null) : null
  const salaire = useSalaire(id, formuleRadar ? idCourant : null)
  const [remuOuvert, setRemuOuvert] = useState(false)

  // Marquer lu à l'ouverture, une seule fois.
  useEffect(() => {
    if (offre.data && !offre.data.read) {
      post(`/api/offers/${id}/read`).then(onChanged).catch(() => {})
    }
  }, [offre.data?.id])

  // Changer d'offre remet la comparaison à zéro : garder une sélection de
  // profils d'une offre à l'autre afficherait un tableau sans rapport avec ce
  // qu'on regarde.
  useEffect(() => {
    setCompare([])
    setSelected(null)
    setOuvert(false)
    setRaisonDonnee(false)
    setRemuOuvert(false)
  }, [id])

  if (offre.loading) return <Loading label={t('offre.loading')} />
  if (offre.error || !offre.data) return <Empty>{offre.error ?? t('offre.notFound')}</Empty>

  const o = offre.data
  // Classement décroissant : la première ligne est celle qu'on présentera dans
  // la grande majorité des cas, elle doit être en haut sans avoir à chercher.
  const candidats = [...o.candidates].sort((a, b) => b.score - a.score)
  const courant = profilCourant(o.candidates, selected)
  const dejaEvalues = new Set(candidats.map((c) => c.consultant_id))
  const restants = (vivier.data ?? []).filter((c) => !dejaEvalues.has(c.id) && c.status === 'available')
  const gesteSalaire = formuleRadar && !!courant && gesteSalaireOffert(salaire.data)
  const salaireDit = o.faits ? salaireEnMots(o.faits, lang) : null
  const ouvrirRemuneration = () => {
    setRemuOuvert(true)
    window.setTimeout(() => document.getElementById('remuneration')?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }), 0)
  }

  // Le dossier COURANT de chaque candidat : le plus récent des siens sur cette
  // offre. `o.dossiers` porte tout l'historique (principe « on garde tout »),
  // cette page ne prend que ce qu'il faut pour savoir QUOI afficher — le
  // détail des versions plus anciennes vit dans `DossierAtelier`.
  const dossierParConsultant = new Map<number, { id: number; created_at: string }>()
  for (const d of o.dossiers) {
    const existant = dossierParConsultant.get(d.consultant_id)
    if (!existant || d.created_at > existant.created_at) dossierParConsultant.set(d.consultant_id, d)
  }
  const dossierDuCourant = courant ? dossierParConsultant.get(courant.consultant_id) : undefined
  const candidatureDuCourant = courant
    ? o.candidatures.find((k) => k.consultantId === courant.consultant_id)
    : undefined

  const compares = compare
    .map((cid) => candidats.find((c) => c.consultant_id === cid))
    .filter((c): c is Candidate => !!c)
  const enComparaison = compares.length >= 2

  function toggleCompare(cid: number) {
    setCompare((prev) => {
      if (prev.includes(cid)) return prev.filter((x) => x !== cid)
      if (prev.length >= MAX_COMPARE) {
        toast(t('offre.compareMax', { n: MAX_COMPARE }), 'err')
        return prev
      }
      return [...prev, cid]
    })
  }

  const retour = retourOffres()

  // Les profils déjà proposés : on ne les repropose pas, la candidature existe.
  const dejaProposes = new Set(o.candidatures.map((k) => k.consultantId))
  const proposables = candidats
    .filter((c) => !dejaProposes.has(c.consultant_id) && !c.archived_at)
    .map((c) => ({ consultantId: c.consultant_id, name: c.name, label: c.label, score: c.score }))

  async function changerStatut(status: OfferStatus) {
    if (status === o.status) return
    await poserStatut(status)
  }

  async function poserStatut(status: OfferStatus) {
    await post(`/api/offers/${id}/status`, { status })
    toast(
      profilUnique
        ? status === 'skip' ? t('offre.radarSkipToast')
          : status === 'later' ? t('offre.radarLaterToast', { date: fullDate(dateRappel(new Date()).toISOString()) })
          : t('offre.radarReopenToast')
        : status === 'skip' ? t('offre.statusSkippedToast')
          : status === 'later' ? t('offre.statusLaterToast')
          : t('offre.statusReopenedToast')
    )
    offre.reload()
    onChanged()
    // Dans une file, « Plus tard » ouvre la suivante. « Pas pour moi » attend
    // la raison, facultative : la choisir, ou « Suivante », fait avancer.
    if (enFile && status === 'later') suivanteDansFile()
  }

  function apresChangement() {
    offre.reload()
    onChanged()
  }

  // Le dossier généré s'affiche EN PLACE : plus de navigation vers une page à
  // part. `offre.reload()` rafraîchit `o.dossiers`, qui fait apparaître le
  // nouveau dossier comme dossier courant de ce candidat, dans la colonne de
  // droite (`courant` ne change pas, c'est ce qui pilote l'affichage).
  async function genererDossier(c: Candidate) {
    setGenerating(c.name)
    try {
      await post<{ id: number }>(`/api/offers/${id}/dossier`, { consultantId: c.consultant_id })
      setSelected(c.consultant_id)
      await offre.reload()
      if (formuleRadar) setFenetreCv('cv')
      onChanged()
    } catch (e: any) {
      toast(e.message, 'err')
    } finally {
      setGenerating(null)
    }
  }

  /**
   * Point d'entrée réel des deux boutons « Générer ». Pose les questions
   * d'écart avant, comme l'extension (maybeAskOfferQuestions), sauf si
   * déjà demandé pour ce couple sur cette visite, ou si l'IA n'en a proposé
   * aucune. Étape bonus : une panne ici ne bloque jamais la génération.
   */
  async function demarrerGeneration(c: Candidate) {
    if (!toujoursDemander && dejaDemande.current.has(c.consultant_id)) {
      await genererDossier(c)
      return
    }
    dejaDemande.current.add(c.consultant_id)
    try {
      const r = await post<{ questions: GapQuestion[] }>(`/api/offers/${id}/gap-questions`, {
        consultantId: c.consultant_id,
      })
      if (r.questions.length > 0) {
        setPendingQuestions({ candidate: c, questions: r.questions })
        return
      }
    } catch {
      // Bonus : une panne sur les questions ne doit jamais empêcher de générer.
    }
    await genererDossier(c)
  }

  async function repondreQuestions(reponses: { topic: string; question: string; detail: string }[]) {
    const pending = pendingQuestions
    if (!pending) return
    setPendingQuestions(null)
    const nonVides = reponses.filter((r) => r.detail.trim())
    if (nonVides.length > 0) {
      try {
        await post(`/api/consultants/${pending.candidate.consultant_id}/enrichment-facts`, { facts: nonVides })
      } catch (e: any) {
        toast(e.message, 'err')
      }
    }
    await genererDossier(pending.candidate)
  }

  function passerQuestions() {
    const pending = pendingQuestions
    if (!pending) return
    setPendingQuestions(null)
    void genererDossier(pending.candidate)
  }

  // « Ce que l'offre demande » (issue #162) : calculé à la première ouverture,
  // gardé avec le score par le serveur, puis relu avec l'offre.
  async function chargerExigences(consultantId: number) {
    try {
      await post(`/api/offers/${id}/exigences`, { consultantId })
      offre.reload()
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  async function postuler(c: Candidate) {
    try {
      await post(`/api/offers/${id}/candidatures`, { consultantId: c.consultant_id })
      toast(t('offre.appliedToast'))
      apresChangement()
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  /**
   * La fiche à une colonne (formule Radar, issue #76) : de quoi parle le poste,
   * pourquoi ce score, qu'est-ce qui me manque, et que fais-je. Dans cet ordre,
   * sans clic : l'en-tête est déjà au-dessus, puis le score DÉPLIÉ (le dessin
   * du widget de l'extension, `ScorePanel` non compact), UN bloc d'action, la
   * description ouverte, et les coulisses (catégories, journal) repliées.
   */
  async function envoyerAvis(positive: boolean) {
    if (!courant) return
    try {
      await post(`/api/offers/${id}/feedback`, { consultantId: courant.consultant_id, positive })
      offre.reload()
    } catch (e: any) {
      toast(e.message, 'err')
    }
  }

  function ficheUneColonne() {
    // « Plus tard » et « Pas pour moi » se défont d'un second clic : le
    // sélecteur à trois issues n'est plus là pour remettre l'offre à décider.
    const basculer = (s: OfferStatus) => poserStatut(o.status === s ? 'todo' : s)
    const aLEntretien = formuleRadar && candidatureDuCourant?.status === 'entretien'
    // Appelé seulement quand le profil courant a un dossier (cf. plus bas).
    const parcours = () => courant && dossierDuCourant && (
      <ParcoursCandidature
        offreId={id}
        dossierId={dossierDuCourant.id}
        consultantId={courant.consultant_id}
        employeur={o.company ?? null}
        urlOffre={o.url}
        score={courant.score}
        scoreEstime={courant.potential_score}
        candidature={candidatureDuCourant}
        rafraichir={lettreEcrite}
        onRedigerLettre={() => setFenetreCv('letter')}
        onChanged={apresChangement}
        carteCv={
          <DossierAtelier
            key={courant.consultant_id}
            offerId={id}
            dossierId={dossierDuCourant.id}
            company={o.company ?? undefined}
            jobDescription={o.description ?? undefined}
            candidature={candidatureDuCourant}
            onChanged={apresChangement}
            onLettreEcrite={() => setLettreEcrite((n) => n + 1)}
            enFenetre
            ouvert={fenetreCv !== null}
            surLettre={fenetreCv === 'letter'}
            onOuvrir={() => setFenetreCv('cv')}
            onFermer={() => setFenetreCv(null)}
          />
        }
      />
    )
    return (
      <div className="fiche1">
        {/* À l'étape entretien (issue #209), la préparation passe en tête :
            c'est ce qu'on vient faire sur la fiche à ce moment-là. */}
        {aLEntretien && <BlocEntretien candidatureId={candidatureDuCourant!.id} />}

        {/* Un avis rédigé dans l'autre langue (issue #168) : le dire, plutôt
            que mélanger les langues sans un mot. Un verdict qui porte sa
            langue sera réécrit par la nuit ; un plus ancien ne le sera pas. */}
        {courant && (() => {
          const ecrit = langueVerdict(courant.result)
          if (!ecrit || ecrit === lang) return null
          return (
            <p className="avis-langue" data-testid="avis-langue">
              {t(({ fr: 'offre.avisEnFrancais', en: 'offre.avisEnAnglais', nl: 'offre.avisEnNeerlandais', it: 'offre.avisEnItalien', es: 'offre.avisEnEspagnol' } as const)[ecrit])}
              {courant.result.lang ? ` ${t('offre.avisRetraduit')}` : ''}
            </p>
          )
        })()}
        {courant && vue.avisResume ? (
          <AvisOffre key={id} offreId={id} result={courant.result} labels={scoreLabels} feedback={courant.feedback} onFeedback={envoyerAvis} />
        ) : courant ? (
          <ScorePanel
            labels={scoreLabels}
            requirementsOpen={vue.exigencesOuvertes}
            feedbackLien={vue.avisEnLien ? { question: t('offre.avisFaux'), merci: t('offre.avisFauxMerci') } : undefined}
            result={courant.result}
            feedback={courant.feedback}
            onFeedback={envoyerAvis}
            onLoadRequirements={() => chargerExigences(courant.consultant_id)}
          />
        ) : monProfil !== null && (
          <EvaluerMonProfil offerId={id} monProfil={monProfil} onDone={offre.reload} />
        )}

        {/* La rémunération (issue #208) : sous l'avis, avant « Vous postulez ? ».
            Ouverte par la pastille de l'en-tête, ou dès qu'une estimation
            existe ou tourne. */}
        {formuleRadar && courant && gesteSalaireOffert(salaire.data) && (remuOuvert || salaire.data?.salaire || salaire.data?.etat !== 'pret') && (
          <BlocRemuneration etat={salaire.data} onLancer={salaire.lancer} />
        )}

        {/* En Radar, dès que le CV est adapté, le parcours en quatre étapes
            remplace la question : elle y deviendrait un doublon. */}
        {courant && !(formuleRadar && dossierDuCourant && !generating) && (generating ? (
          <div className="genwait">
            <span className="spinner big" />
            <div className="gw">{t('offre.generatingMyCv')}</div>
            <div>{t('offre.generatingHelp')}</div>
          </div>
        ) : (
          <div className={`fiche-actions${candidatureDuCourant ? '' : ' vp'}`} role="group" aria-label={t('offre.statusAriaLabel')}>
            {candidatureDuCourant ? (
              // Une fois postulé, l'étape remplace le tri : l'offre a quitté la
              // pile, la question n'est plus « j'y vais ? » mais « où j'en suis ».
              // Avec un CV adapté, c'est l'atelier plus bas qui porte l'étape.
              !dossierDuCourant && (
                <span className="fa-etat">
                  <Check size={14} />{t('offre.alreadyInApplications')}
                  <SelecteurEtape
                    candidatureId={candidatureDuCourant.id}
                    status={candidatureDuCourant.status}
                    onChanged={apresChangement}
                  />
                </span>
              )
            ) : (
              // UNE QUESTION, PAS SIX BOUTONS (lot 2 de la refonte). La fiche
              // est la page d'arrivée des mails, lue au téléphone : la question
              // reste collée en bas de l'écran (`.vp`), et la réponse « oui »
              // commence par le geste qui sert, adapter le CV. Postuler sans
              // l'adapter reste possible, en lien.
              <>
                {/* Venu du mail par « Plus tard » ou « Pas pour moi » : le lien
                    ne tranche rien (un antivirus l'aurait ouvert), il demande
                    de confirmer d'un clic, ici. */}
                {formuleRadar && o.status === 'todo' && (actionLien === 'later' || actionLien === 'skip') && (
                  <span className="vp-lien">{t(actionLien === 'later' ? 'offre.lienLater' : 'offre.lienSkip')}</span>
                )}
                <b className="vp-q">{dossierDuCourant ? t('offre.vpCvPret') : t('offre.vpQuestion')}</b>
                {dossierDuCourant ? (
                  <ActionButton variant="primary" onClick={() => postuler(courant)}>
                    <Send size={14} />{mots('etat.postuler')}
                  </ActionButton>
                ) : (
                  <ActionButton variant="primary" onClick={() => demarrerGeneration(courant)}>
                    <Sparkles size={14} />{t('offre.vpOui')}
                  </ActionButton>
                )}
                <span className="vp-autres">
                  <button
                    type="button"
                    className={`btn-secondary${o.status === 'later' ? ' on' : ''}${o.status === 'todo' && actionLien === 'later' ? ' vp-attendu' : ''}`}
                    aria-pressed={o.status === 'later'}
                    onClick={() => basculer('later')}
                  >
                    <Bookmark size={14} />{mots('etat.later')}
                  </button>
                  <button
                    type="button"
                    className={`btn-secondary${o.status === 'skip' ? ' on' : ''}${o.status === 'todo' && actionLien === 'skip' ? ' vp-attendu' : ''}`}
                    aria-pressed={o.status === 'skip'}
                    onClick={() => basculer('skip')}
                  >
                    <X size={14} />{mots('etat.skip')}
                  </button>
                </span>
                {!dossierDuCourant && (
                  <button type="button" className="linkbtn vp-sans" onClick={() => postuler(courant)}>
                    {t('offre.vpSansAdapter')}
                  </button>
                )}
                {/* Pourquoi pas ? Facultatif, une liste fermée qui se compte. */}
                {formuleRadar && o.status === 'skip' && !raisonDonnee && (
                  <span className="vp-raison">
                    <span className="acc-gris">{t('raison.question')}</span>
                    {(['loin', 'metier', 'salaire', 'employeur', 'autre'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        className="chip"
                        onClick={async () => {
                          await post(`/api/offers/${id}/status`, { status: 'skip', raison: r })
                          setRaisonDonnee(true)
                          toast(t('raison.merci'))
                          if (enFile) suivanteDansFile()
                        }}
                      >
                        {t(`raison.${r}` as const)}
                      </button>
                    ))}
                  </span>
                )}
              </>
            )}
          </div>
        ))}

        {/* Sous l'action (issue #169) : ce que l'offre demande, ouvert, chaque
            exigence avec son mot (« Couvert », « À confirmer », « Manquant »),
            puis le détail du score replié. */}
        {courant && vue.avisResume && (
          <>
            <ExigencesUneFois
              key={id}
              offreId={id}
              requirements={courant.result.requirements}
              onLoad={() => chargerExigences(courant.consultant_id)}
              open={vue.exigencesOuvertes}
              titre={scoreLabels.requirements}
              attente={scoreLabels.requirementsLoading}
              vide={scoreLabels.requirementsEmpty}
              mots={{ met: t('offre.exigenceCouverte'), partial: t('offre.exigenceAConfirmer'), missing: t('offre.exigenceManquante') }}
            />
            <details className="pool plie-score">
              <summary className="pool-h">
                <ChevronRight size={13} className="desc-chevron" />{t('offre.detailScore')}
              </summary>
              <ScorePanel labels={scoreLabels} result={courant.result} masquerTete masquerExigences />
            </details>
          </>
        )}

        {/* La relance à J+7 (lot 3) : Radar seulement, une candidature
            envoyée sans nouvelles. Le lien du mail précise quoi ouvrir. */}
        {formuleRadar && candidatureDuCourant && !generating && (
          <BlocRelance
            candidatureId={candidatureDuCourant.id}
            action={(['relance', 'reponse', 'abandon'] as const).find((a) => a === parseQuery(window.location.hash).get('a')) ?? null}
            onChanged={apresChangement}
          />
        )}
        {/* Le parcours en quatre étapes (lot 3) : dès que le CV est adapté.
            À l'étape entretien, il est fini : replié en une ligne (#209). */}
        {formuleRadar && courant && dossierDuCourant && !generating && (aLEntretien ? (
          <details className="pool parcours-replie" data-testid="parcours-replie">
            <summary className="pool-h">
              <ChevronRight size={13} className="desc-chevron" />{t('entretien.parcours')}
            </summary>
            {parcours()}
          </details>
        ) : parcours())}

        {!formuleRadar && courant && dossierDuCourant && !generating && (
          <DossierAtelier
            key={courant.consultant_id}
            offerId={id}
            dossierId={dossierDuCourant.id}
            company={o.company ?? undefined}
            jobDescription={o.description ?? undefined}
            candidature={candidatureDuCourant}
            onChanged={apresChangement}
            onLettreEcrite={() => setLettreEcrite((n) => n + 1)}
          />
        )}

        {vue.annonceRepliee ? (
          <details className="pool fiche-desc">
            <summary className="pool-h">
              <ChevronRight size={13} className="desc-chevron" />{t('offre.annonceComplete')}
            </summary>
            <div className="offer-desc">
              {o.description ? nettoyerDescription(o.description) : t('offre.noDescriptionCaptured')}
            </div>
          </details>
        ) : (
          <section className="pool fiche-desc">
            <div className="pool-h">{t('offre.descriptionTitle')}</div>
            <div className="offer-desc">
              {o.description ? nettoyerDescription(o.description) : t('offre.noDescriptionCaptured')}
            </div>
          </section>
        )}

        <details className="pool coulisses">
          <summary className="pool-h">
            <ChevronRight size={13} className="desc-chevron" />{t('offre.behindTheScenes')}
          </summary>
          {/* Même droit d'édition qu'avant le repli : seul le BM range les
              annonces, un particulier vérifie pourquoi une offre lui arrive. */}
          <Categories base={`/api/offers/${id}`} editable={!profilUnique} onChanged={offre.reload} />
          {o.events.length > 0 && (
            <>
              <div className="pool-h"><History size={13} />{t('offre.journal')}</div>
              <div className="journal timeline">
                {o.events.map((e) => (
                  <div className={`tev${e.type.startsWith('candidature.') ? ' teal' : ''}`} key={e.id}>
                    <div className="td">{dateHeure(e.created_at)} · {e.author}</div>
                    <div className="tt"><b>{EVENEMENTS[e.type] ?? e.type}</b></div>
                  </div>
                ))}
              </div>
            </>
          )}
        </details>
      </div>
    )
  }

  return (
    <>
      <div className="bar">
        <button className="back" onClick={() => navigate(retour.href)}>
          <ChevronLeft size={15} />{mots(retour.cle)}
        </button>
        <div className="grow" />
        {vue.selecteurStatut && <>
        {/* Les trois issues de tri sont montrées ENSEMBLE, et celle en cours
            est enfoncée. L'écran d'avant n'offrait qu'une bascule « pas
            pertinent / remettre à traiter » : le statut affiché disait une
            chose, le bouton en proposait une autre, et il fallait lire les deux
            pour savoir où on en était. */}
        <div className="segstatus" role="group" aria-label={t('offre.statusAriaLabel')}>
          {(Object.keys(OFFER_STATUS) as OfferStatus[]).map((s) => (
            <button
              key={s}
              className={`seg st-${s}${o.status === s ? ' on' : ''}`}
              aria-pressed={o.status === s}
              onClick={() => changerStatut(s)}
            >
              <span className="sd" />{OFFER_STATUS[s]}
            </button>
          ))}
        </div>
        </>}
      </div>

      <div className={`body${vue.uneColonne ? ' body-fiche1' : ''}`}>
        {enFile && (
          <nav className="file-nav" aria-label={t('file.aria')} data-testid="file-nav">
            <span>{t('file.position', { rang: enFile.rang, total: enFile.total })}</span>
            <a href={enFile.suivante ?? FIN_DE_FILE}>
              {enFile.suivante ? t('file.suivante') : t('file.terminer')}<ChevronRight size={14} />
            </a>
          </nav>
        )}
        {/* --- L'offre, en bandeau : c'est le contexte de tout le reste, elle
                ne peut pas tenir dans une colonne de 300 px. --- */}
        <div className="offer-hero">
          <div className="offer-top">
            <LogoEntreprise nom={o.company} logo={o.logo} className="offer-logo" />
            <div className="offer-id">
              <div className="oc">
                <Employeur nom={o.company} source={o.source_name} />
                {/* Le genre de l'offre, visible AU MÊME ENDROIT dans les deux
                    formules, jamais deviné : il vient de `sources.kind`, posé
                    une fois pour toutes à l'ajout de la source, jamais de l'IA.
                    Un badge manquant se lirait « on ne sait pas », donc il est
                    toujours là, y compris pour dire « Emploi ». */}
                {(vue.badgeGenre || o.kind === 'mission') && (
                  <span className={`kindbadge kb-${o.kind}`}>{o.kind === 'mission' ? t('offre.kindMission') : t('offre.kindJob')}</span>
                )}
              </div>
              <h2>{o.title}</h2>
            </div>
          </div>

          <div className="offer-chips">
            {o.location && <span className="ochip"><MapPin />{o.location}</span>}
            {/* Ce que l'offre dit d'elle-même (issue #169) : contrat,
                télétravail, salaire, langues. Rien quand elle se tait. */}
            {faitsEnMots(o.faits, lang)
              .filter((f) => !(gesteSalaire && f === salaireDit))
              .map((f) => <span key={f} className="ochip fait">{f}</span>)}
            {/* La pastille salaire porte le geste (issue #208) : « Comparer »
                quand l'offre annonce un montant, « Estimer » sinon. */}
            {gesteSalaire && (
              <button type="button" className="ochip fait link geste-salaire" data-testid="pastille-salaire" onClick={ouvrirRemuneration}>
                <Coins />{salaireDit ? `${salaireDit} · ${t('remu.comparer')}` : `${t('remu.nonAnnonce')} · ${t('remu.estimer')}`}
              </button>
            )}
            {/* La provenance, sauf si elle remplace déjà l'employeur en titre,
                ou si elle le répète (« IBA » sous « IBA », issue #168). Au
                téléphone, la pastille salaire la remplace : l'en-tête la dit. */}
            {o.company && !memeNom(o.company, o.source_name) && <span className={`ochip${gesteSalaire ? ' provenance' : ''}`}><Rss />{o.source_name}</span>}
            {/* La date où Radar l'a trouvée : « Vue » se lisait « vue par moi ». */}
            <span className="ochip"><Clock />{t('offre.reperee', { date: jourCourt(parseDate(o.first_seen_at) ?? new Date()) })}</span>
            {/* Lue quand personne ne suivait son site, pas relue depuis (issue #131). */}
            {o.sourceARelire && (
              <span className="ochip vieux"><CircleAlert />{t('offre.staleSource', { date: jourDeLecture(o.sourceARelire) })}</span>
            )}
            {/* Republiée, ou en ligne depuis longtemps (issue #161) : un constat. */}
            {mentionSignaux(o.signaux, t) && (
              <span className="ochip vieux" data-testid="offre-signaux"><CircleAlert />{mentionSignaux(o.signaux, t)}</span>
            )}
            {vue.candidats && <span className="ochip"><Users />{t(candidats.length > 1 ? 'offre.evaluatedProfilesPlural' : 'offre.evaluatedProfilesSingular', { n: candidats.length })}</span>}
            {o.url && (
              <a className="ochip link" href={o.url} target="_blank" rel="noopener">
                <ExternalLink />{t('offre.originalPosting')}
              </a>
            )}
          </div>

          {/* Les champs propres à une MISSION. Chaque valeur absente affiche
              « non renseigné », jamais un blanc silencieux : c'est le point
              explicitement demandé par Nicolas, et c'est cohérent avec le
              reste du dépôt (cf. CLAUDE.md « un score gardé… »). Un portail de
              mission peut très bien ne pas donner de date de fin ; ça ne veut
              pas dire que l'extraction a échoué. */}
          {o.kind === 'mission' && (
            <div className="offer-chips mission-chips">
              <span className={`ochip${tempsRestant(o.deadline_at).urgent ? ' urgent' : ''}`}>
                <Clock />{t('offre.deadlineValue', { value: o.deadline_at ? `${fullDate(o.deadline_at)} (${tempsRestant(o.deadline_at).texte})` : t('offre.notSetFem') })}
              </span>
              <span className="ochip">
                <CalendarDays />{t('offre.startValue', { value: o.starts_at ? fullDate(o.starts_at) : t('offre.notSetMasc') })}
              </span>
              <span className="ochip">
                <CalendarDays />{t('offre.endValue', { value: o.ends_at ? fullDate(o.ends_at) : t('offre.notSetFem') })}
              </span>
              <span className="ochip">
                <Swords />
                {typeof o.proposals_count === 'number'
                  ? t(o.proposals_count > 1 ? 'offre.proposalsPlural' : 'offre.proposalsSingular', { n: o.proposals_count })
                  : t('offre.proposalsUnset')}
              </span>
              <span className="ochip">
                <Building2 />{o.framework || t('offre.frameworkUnset')}
              </span>
            </div>
          )}

          {/* Repliée par défaut : elle domine l'écran depuis toujours, alors
              qu'elle n'est que le contexte, pas ce qu'on vient lire ici. Sa
              propre zone de défilement plutôt qu'un fondu tronqué : lire la
              description au complet ne doit pas pousser tout le reste de la
              colonne plus bas. Les retours chariot des annonces scrapées sont
              conservés tels quels en base, d'où `pre-line`. */}
          {!vue.descriptionDepliee && (
            <button
              type="button"
              className="desc-toggle"
              aria-expanded={ouvert}
              onClick={() => setOuvert(!ouvert)}
            >
              <ChevronRight size={13} className="desc-chevron" />
              {ouvert ? t('offre.collapseDesc') : t('offre.expandDesc')}
            </button>
          )}
          {ouvert && !vue.descriptionDepliee && (
            <div className="offer-desc">
              {o.description ? nettoyerDescription(o.description) : t('offre.noDescriptionCaptured')}
            </div>
          )}
        </div>

        {vue.uneColonne ? ficheUneColonne() : (
        <div className="ws">
          <div className="wsL">
            {/* LES CANDIDATURES D'ABORD. Ce que je fais réellement sur cette
                offre passe avant le classement de ce que la machine propose :
                dès qu'un profil est engagé, c'est lui la question. */}
            {o.candidatures.length > 0 && (
              <div className="pool">
                <div className="pool-h">
                  <Send size={13} />{t('offre.applicationsHeader')}<span className="n">{o.candidatures.length}</span>
                </div>
                {o.candidatures.map((k) => (
                  <div className="cand kcand" key={k.id}>
                    <span className="miniav" style={teinte(k.name)}>{initials(k.name)}</span>
                    <span className="grow">
                      <span className="mn">{k.name}</span>
                      <span className="mm">
                        {t('offre.proposedAgo', { when: relTime(k.created_at) })}
                        {k.dossierId && (
                          <>
                            {' · '}
                            <button className="linkbtn" onClick={() => setSelected(k.consultantId)}>
                              {t('offre.viewFileLink')}
                            </button>
                          </>
                        )}
                      </span>
                    </span>
                    <SelecteurEtape candidatureId={k.id} status={k.status} onChanged={apresChangement} />
                  </div>
                ))}
              </div>
            )}

            <div className="pool">
              <div className="pool-h">
                {t('offre.candidats')}
                {!profilUnique && <span className="n">{candidats.length}</span>}
                <span className="grow" />
                {/* Proposer sans passer par le panneau de droite : sur une
                    offre où le premier est évident, la décision se prend d'ici.
                    Rien à proposer en Radar : on ne présente pas quelqu'un, on
                    postule, et ça se fait depuis le panneau de droite. */}
                {!profilUnique && (
                  <ProposerProfil offerId={id} profils={proposables} onDone={apresChangement} />
                )}
              </div>

              {candidats.length === 0 ? (
                <Empty>
                  {t('offre.aucunCandidat')}
                </Empty>
              ) : (
                candidats.map((c, i) => {
                  const coche = compare.includes(c.consultant_id)
                  const gain = c.potential_score - c.score
                  return (
                    <div
                      key={c.consultant_id}
                      className={`cand${courant?.consultant_id === c.consultant_id && !enComparaison ? ' sel' : ''}${coche ? ' cmp' : ''}`}
                    >
                      {/* CLASSER ET COMPARER n'ont de sens qu'à plusieurs. En
                          Radar il n'y a qu'un profil : un rang « 1 » sur une
                          liste d'un seul, et une case pour se comparer à
                          soi-même, sont des commandes qui ne peuvent rien
                          produire. Elles ne sont pas cachées par pudeur, elles
                          n'ont pas d'objet. */}
                      {!profilUnique && <span className="rank">{i + 1}</span>}
                      {!profilUnique && (
                        <Checkbox
                          checked={coche}
                          onChange={() => toggleCompare(c.consultant_id)}
                          label={t('offre.compareName', { name: c.name })}
                        />
                      )}
                      <button className="cand-main" onClick={() => setSelected(c.consultant_id)}>
                        <ScoreRing
                          score={c.score}
                          potential={c.potential_score}
                          tone={scoreTone(c.score)}
                          size={38}
                          ariaLabel={t('offre.scoreAriaLabel', { name: c.name, score: c.score })}
                        />
                        <span className="grow">
                          <span className="mn">
                            {c.name}
                            {c.affinity === null && (
                              <span className="ondemand-tag" title={t('offre.onDemandTitle')}> ·</span>
                            )}
                          </span>
                          <span className="mm">{c.label}</span>
                          {gain > 0 && <span className="up">{t('offre.gainAfterAdaptation', { gain })}</span>}
                        </span>
                        {/* Un dossier maximum par (offre, candidat) : ce point
                            dit s'il en existe un, jamais lequel ni combien de
                            versions — ce détail vit dans l'atelier lui-même. */}
                        {dossierParConsultant.has(c.consultant_id) && (
                          <span className="doss-dot" title={t('offre.fileGeneratedTitle')} />
                        )}
                        <ChevronRight size={15} className="cand-open" />
                      </button>
                    </div>
                  )
                })
              )}

              {!profilUnique && candidats.length >= 2 && (
                <div className="cmphint">
                  {t('offre.compareHintBase')}
                  {compare.length === 0 ? (
                    <>
                      {' '}{t('offre.compareOr')}{' '}
                      <button
                        className="linkbtn"
                        onClick={() => setCompare(candidats.slice(0, 3).map((c) => c.consultant_id))}
                      >
                        {t('offre.compareBest', { n: Math.min(3, candidats.length) })}
                      </button>
                      .
                    </>
                  ) : (
                    t('offre.compareFreeSuffix')
                  )}
                </div>
              )}

              {/* Deux intentions différentes derrière le même endpoint : le BM
                  repêche quelqu'un que le pré-filtre a écarté, le particulier
                  se mesure à une offre que personne n'a regardée pour lui.
                  En Radar le bloc ne s'affiche que s'il n'y a PAS déjà un
                  score : une fois évalué, se réévaluer ne rendrait pas le même
                  chiffre, et c'est tout ce que le cache existe pour éviter. */}
              {/* Les catégories de l'OFFRE : c'est le second bout de la corde.
                  Une offre mal rangée n'atteint personne, et jusqu'ici ça se
                  lisait « le vivier ne correspond à rien ». Toujours affichées,
                  y compris en Radar : un particulier doit pouvoir vérifier
                  pourquoi une offre lui est (ou non) proposée. Seule l'ÉDITION
                  est réservée au BM en Radar : un particulier n'a pas à ranger
                  les annonces des autres. */}
              <Categories base={`/api/offers/${id}`} editable={!profilUnique} onChanged={offre.reload} />

              {profilUnique
                ? monProfil !== null && candidats.length === 0 && (
                    <EvaluerMonProfil offerId={id} monProfil={monProfil} onDone={offre.reload} />
                  )
                : (
                  <>
                    <ProfilsVoisins offerId={id} onDone={apresChangement} />
                    {restants.length > 0 && (
                      <EvaluerAutre offerId={id} restants={restants} onDone={offre.reload} />
                    )}
                  </>
                )}
            </div>

            {/* Le journal EST la source de vérité des statuts (principe n°1 du
                schéma). Dates condensées : sur une session de travail, tout
                est souvent arrivé le même jour, c'est l'heure qui distingue,
                pas la date répétée huit fois. */}
            {o.events.length > 0 && (
              <div className="pool">
                <div className="pool-h"><History size={13} />{t('offre.journal')}</div>
                <div className="journal timeline">
                  {o.events.map((e) => (
                    <div className={`tev${e.type.startsWith('candidature.') ? ' teal' : ''}`} key={e.id}>
                      <div className="td">{dateHeure(e.created_at)} · {e.author}</div>
                      <div className="tt"><b>{EVENEMENTS[e.type] ?? e.type}</b></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="wsR">
            {generating ? (
              <div className="genwait">
                <span className="spinner big" />
                <div className="gw">{t('offre.generatingFile', { name: generating })}</div>
                <div>{t('offre.generatingHelp')}</div>
              </div>
            ) : enComparaison ? (
              <>
                <div className="cmpbar">
                  <GitCompare size={15} />
                  <span className="grow">{t('offre.comparedCount', { n: compares.length })}</span>
                  <button className="linkbtn" onClick={() => setCompare([])}>{t('offre.closeComparison')}</button>
                </div>
                <ScoreCompare
                  labels={compareLabels}
                  subjects={compares.map(
                    (c): CompareSubject => ({
                      id: c.consultant_id,
                      name: c.name,
                      role: c.label,
                      result: c.result,
                    })
                  )}
                  onRemove={(cid) => setCompare((prev) => prev.filter((x) => x !== cid))}
                  renderAction={(s) => {
                    const c = compares.find((x) => x.consultant_id === s.id)!
                    return (
                      <ActionButton variant="primary" size="sm" onClick={() => demarrerGeneration(c)}>
                        <Sparkles size={13} />{t('offre.generateTheirFile')}
                      </ActionButton>
                    )
                  }}
                />
              </>
            ) : courant ? (
              <>
                <ScorePanel
                  compact
                  labels={scoreLabels}
                  result={courant.result}
                  subject={courant.name}
                  role={courant.label}
                  feedback={courant.feedback}
                  onFeedback={async (positive) => {
                    try {
                      await post(`/api/offers/${id}/feedback`, { consultantId: courant.consultant_id, positive })
                      offre.reload()
                    } catch (e: any) {
                      toast(e.message, 'err')
                    }
                  }}
                  onLoadRequirements={() => chargerExigences(courant.consultant_id)}
                />
                {/* Le dossier COURANT du candidat sélectionné, en place — plus
                    de navigation vers une page à part. Un candidat, un
                    dossier, pas une liste : cf. note de tête du fichier. */}
                {dossierDuCourant ? (
                  <DossierAtelier
                    key={courant.consultant_id}
                    offerId={id}
                    dossierId={dossierDuCourant.id}
                    company={o.company ?? undefined}
                    jobDescription={o.description ?? undefined}
                    candidature={candidatureDuCourant}
                    onChanged={apresChangement}
                  />
                ) : (
                <div className="gen-cta">
                  <div className="gt">
                    {/* DEUX GESTES DISTINCTS, et l'ordre compte. Le premier ne
                        coûte rien et fait entrer la candidature au suivi ;
                        générer le CV adapté est l'étape payante. Les confondre
                        est exactement ce qui rendait le statut d'une offre
                        dépendant d'un appel IA.
                        Le RÉCIT change complètement d'un produit à l'autre :
                        un business manager présente quelqu'un à un client, un
                        particulier postule pour lui-même. « Prêt à présenter
                        Amélie ? » posé à Amélie n'a aucun sens. */}
                    {profilUnique ? (
                      <>
                        <h4>{t('offre.applyTitle')}</h4>
                        <p>
                          <b>{mots('etat.postuler')}</b> {t('offre.applyBody1')}
                          <b> {t('offre.adaptCvLabel')}</b> {t('offre.applyBody2')}
                        </p>
                      </>
                    ) : (
                      <>
                        <h4>{t('offre.presentTitle', { name: courant.name.split(' ')[0] })}</h4>
                        <p>
                          <b>{t('candidature.propose')}</b> {t('offre.presentBody1')}
                          {' '}<b>{t('offre.theFileLabel')}</b> {t('offre.presentBody2')}
                        </p>
                      </>
                    )}
                  </div>
                  {dejaProposes.has(courant.consultant_id) ? (
                    <span className="hint">{profilUnique ? t('offre.alreadyInApplications') : t('offre.alreadyProposed')}</span>
                  ) : (
                    <ProposerProfil
                      offerId={id}
                      variant="primary"
                      profils={[{
                        consultantId: courant.consultant_id,
                        name: courant.name,
                        label: courant.label,
                        score: courant.score,
                      }]}
                      onDone={apresChangement}
                    />
                  )}
                  <span className="hint"><Clock size={12} />{t('offre.about15s')}</span>
                  <button className="btn-secondary btn-lg" onClick={() => demarrerGeneration(courant)}>
                    <Sparkles size={15} />{t('offre.generateAdaptedFile')}
                  </button>
                  <CompteurAdaptations />
                </div>
                )}
              </>
            ) : (
              <Empty>{t('offre.emptyAnalysis')}</Empty>
            )}
          </div>
        </div>
        )}
      </div>

      {pendingQuestions && (
        <QuestionsEcart
          questions={pendingQuestions.questions}
          onSubmit={repondreQuestions}
          onSkip={passerQuestions}
        />
      )}
    </>
  )
}

/**
 * Les profils des catégories voisines, évaluables d'un clic.
 *
 * C'EST CE QUI REND LA PORTE AU N2 TENABLE. La catégorie décide qui est évalué
 * automatiquement ; ce composant montre qui est juste à côté et ne l'a pas été.
 * Sans lui, la catégorisation redeviendrait un tri invisible, avec un autre
 * mécanisme mais le même défaut : on ne saurait pas qui on rate.
 *
 * Volontairement REPLIÉ par défaut. C'est un rattrapage, pas le cas courant :
 * l'ouvrir d'office donnerait à la liste principale l'air d'être incomplète.
 */
function ProfilsVoisins({ offerId, onDone }: { offerId: number; onDone: () => void }) {
  const voisins = useData(
    () => get<{ consultantId: number; name: string; labels: string[] }[]>(`/api/offers/${offerId}/voisins`),
    [offerId]
  )
  const toast = useToast()
  const t = useT()
  if (!voisins.data || voisins.data.length === 0) return null
  return (
    <details className="ondemand">
      <summary className="pool-h">
        {t('offre.neighboursTitle')}<span className="n">{voisins.data.length}</span>
      </summary>
      <p className="field-help">
        {t('offre.neighboursHelp')}
      </p>
      <div className="catlist">
        {voisins.data.map((v) => (
          <div key={v.consultantId} className="catitem">
            <div className="grow">
              <div className="cat-label">{v.name}</div>
              <div className="cat-chemin">{v.labels.join(', ')}</div>
            </div>
            <ActionButton
              busyLabel=""
              onClick={async () => {
                try {
                  const r = await post<{ score: number }>(`/api/offers/${offerId}/score-consultant`, {
                    consultantId: v.consultantId,
                  })
                  toast(t('offre.scoredToast', { name: v.name, score: r.score }))
                  onDone()
                } catch (e: any) { toast(e.message, 'err') }
              }}
            >
              {t('offre.evaluate')}
            </ActionButton>
          </div>
        ))}
      </div>
    </details>
  )
}

/**
 * S'évaluer soi-même sur une offre (formule Radar).
 *
 * MÊME endpoint que `EvaluerAutre`, mais sans liste déroulante : il n'y a
 * qu'un profil possible, et le proposer dans un menu à une entrée ferait
 * choisir entre soi et soi. Surtout, l'intention n'est pas la même. Le BM
 * repêche quelqu'un que le pré-filtre a écarté ; le particulier veut juste
 * savoir où il en est sur une offre que personne n'a encore regardée pour lui.
 */
function EvaluerMonProfil({
  offerId,
  monProfil,
  onDone,
}: {
  offerId: number
  monProfil: number
  onDone: () => void
}) {
  const toast = useToast()
  const t = useT()
  return (
    <div className="ondemand">
      <div className="pool-h">{t('offre.notYetEvaluated')}</div>
      <div className="ondemand-row">
        <ActionButton
          busyLabel={t('offre.evaluatingLabel')}
          onClick={async () => {
            try {
              const r = await post<{ score: number; alreadyScored: boolean }>(
                `/api/offers/${offerId}/score-consultant`,
                { consultantId: monProfil }
              )
              toast(t(r.alreadyScored ? 'offre.alreadyScoredToast' : 'offre.evaluatedToast', { score: r.score }))
              onDone()
            } catch (e: any) { toast(e.message, 'err') }
          }}
        >
          {t('offre.evaluateMyProfile')}
        </ActionButton>
      </div>
      <div className="field-help">{t('offre.evaluateHelp')}</div>
    </div>
  )
}

/** Évaluer un profil que le pré-filtre n'a pas proposé. C'est ce qui fait
 *  qu'il ne cache jamais personne définitivement. */
function EvaluerAutre({
  offerId,
  restants,
  onDone,
}: {
  offerId: number
  restants: ConsultantRow[]
  onDone: () => void
}) {
  const [who, setWho] = useState(String(restants[0]?.id ?? ''))
  const toast = useToast()
  const t = useT()
  return (
    <div className="ondemand">
      <div className="pool-h">{t('offre.evaluateAnother')}</div>
      <div className="ondemand-row">
        <select className="chip grow" value={who} onChange={(e) => setWho(e.target.value)}>
          {restants.map((c) => (
            <option key={c.id} value={c.id}>{c.name} — {c.label.slice(0, 34)}</option>
          ))}
        </select>
        <ActionButton
          busyLabel=""
          onClick={async () => {
            try {
              const r = await post<{ score: number; alreadyScored: boolean }>(
                `/api/offers/${offerId}/score-consultant`,
                { consultantId: Number(who) }
              )
              toast(t(r.alreadyScored ? 'offre.alreadyScoredToast' : 'offre.evaluatedToast', { score: r.score }))
              onDone()
            } catch (e: any) { toast(e.message, 'err') }
          }}
        >
          {t('offre.evaluate')}
        </ActionButton>
      </div>
      <div className="field-help">{t('offre.evaluateHelp')}</div>
    </div>
  )
}

/** Deux noms qui désignent le même employeur : « IBA » et « IBA », ou « BNP
 *  Paribas Fortis » et « BNP Paribas Fortis (carrières) ». Casse, accents et
 *  ponctuation ignorés. */
function memeNom(a: string, b: string): boolean {
  const n = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const x = n(a)
  const y = n(b)
  return !!x && !!y && (x === y || y.startsWith(x + ' ') || x.startsWith(y + ' '))
}

/** `ExigencesCitees` qui se coche la première fois qu'on voit l'offre (#187).
 *  La décision ne se prend que quand les lignes sont là et le bloc ouvert :
 *  un bloc fermé (console) ou un chargement abandonné ne grille pas
 *  l'animation. `key` la reprend sur « Suivante ». */
function ExigencesUneFois({ offreId, ...props }: { offreId: number } & Parameters<typeof ExigencesCitees>[0]) {
  const decision = useRef<boolean | null>(null)
  if (decision.current === null && props.open && (props.requirements?.length ?? 0) > 0) {
    decision.current = premiereVueDesExigences(offreId)
  }
  return <ExigencesCitees {...props} anime={decision.current === true} />
}

/** Le profil affiché : celui qu'on a choisi, sinon le mieux noté. */
function profilCourant<C extends { consultant_id: number; score: number }>(candidates: C[], selected: number | null): C | null {
  const tries = [...candidates].sort((a, b) => b.score - a.score)
  return tries.find((c) => c.consultant_id === selected) ?? tries[0] ?? null
}
